import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Folder, FolderOpen, Loader2, Home } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
const btn = "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition disabled:opacity-60";

/** Create or rename a folder / rename a file. */
export function NameDialog({ open, onClose, title, description, initial = "", confirmLabel = "Save", onSubmit, testid = "name-dialog" }) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setName(initial); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try { await onSubmit(name.trim()); onClose(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={testid}>
        <form onSubmit={submit}>
          <DialogHeader><DialogTitle>{title}</DialogTitle>{description && <DialogDescription>{description}</DialogDescription>}</DialogHeader>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} maxLength={160} className={`${inputCls} my-4`} data-testid={`${testid}-input`} />
          <DialogFooter>
            <button type="button" onClick={onClose} className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}>Cancel</button>
            <button type="submit" disabled={busy || !name.trim()} className={`${btn} bg-primary text-white hover:shadow`} data-testid={`${testid}-submit`}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {confirmLabel}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Pick a destination folder (tree) for moving a folder or file. */
export function MoveDialog({ open, onClose, scope, item, onMove }) {
  const [tree, setTree] = useState([]);
  const [dest, setDest] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDest(null);
    api.get("/docs/tree", { params: { scope } }).then((r) => setTree(r.data)).catch(() => setTree([]));
  }, [open, scope]);

  const excluded = new Set();
  if (item?.kind === "folder") {
    const walk = (id) => { excluded.add(id); tree.filter((f) => f.parent_id === id).forEach((c) => walk(c.id)); };
    walk(item.id);
  }
  const currentParent = item?.kind === "folder" ? item.parent_id : item?.folder_id;

  const render = (parentId, depth) => tree.filter((f) => (f.parent_id || null) === parentId && !excluded.has(f.id)).map((f) => (
    <div key={f.id}>
      <button type="button" onClick={() => setDest(f.id)} data-testid={`move-dest-${f.id}`}
        className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left hover:bg-slate-100 ${dest === f.id ? "bg-primary/10 text-primary font-medium" : "text-slate-700"}`}
        style={{ paddingLeft: 8 + depth * 18 }}>
        {dest === f.id ? <FolderOpen className="h-4 w-4 shrink-0" /> : <Folder className="h-4 w-4 shrink-0 text-amber-500" />} <span className="truncate">{f.name}</span>
      </button>
      {render(f.id, depth + 1)}
    </div>
  ));

  const submit = async () => {
    setBusy(true);
    try { await onMove(dest); onClose(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Move failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="move-dialog">
        <DialogHeader><DialogTitle>Move “{item?.name}”</DialogTitle><DialogDescription>Choose a destination folder.</DialogDescription></DialogHeader>
        <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 p-1.5 my-2">
          <button type="button" onClick={() => setDest(null)} data-testid="move-dest-root"
            className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left hover:bg-slate-100 ${dest === null ? "bg-primary/10 text-primary font-medium" : "text-slate-700"}`}>
            <Home className="h-4 w-4 shrink-0" /> Top level
          </button>
          {render(null, 1)}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}>Cancel</button>
          <button type="button" onClick={submit} disabled={busy || (dest ?? null) === (currentParent ?? null)} className={`${btn} bg-primary text-white hover:shadow`} data-testid="move-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Move here
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
